import React, { useState, useEffect } from "react";
import { CustomFormInputProps } from "./FormInput";
import FormInputPassword from "./FormInputPassword";
import { useTranslation } from "react-i18next";
import { useField } from "formik";
import {
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import { CheckCircleOutline, ErrorOutline } from "@material-ui/icons";
import {
  minLength,
  hasUpperCase,
  hasLowerCase,
  hasNumbers,
  hasSpecialCharacters,
} from "../../utils/passwordRules";

type CheckProps = {
  rule: Function;
  testid: string;
};

const RuleCheck: React.FC<CheckProps> = ({ rule, testid }) => {
  const { t } = useTranslation();
  const [valid, setValid] = useState(false);
  const [{ value }] = useField("password");
  const label = rule(t).describe().label;

  useEffect(() => {
    rule(t)
      .validate(value)
      .then(() => {
        setValid(true);
      })
      .catch(() => {
        setValid(false);
      });
  }, [value, rule, t]);

  return (
    <ListItem disableGutters dense data-testid={testid}>
      <ListItemIcon style={{ minWidth: "32px" }}>
        {valid ? (
          <CheckCircleOutline color="secondary" />
        ) : (
          <ErrorOutline color="disabled" />
        )}
      </ListItemIcon>
      <ListItemText data-testid={`${testid}Label`}>{label}</ListItemText>
    </ListItem>
  );
};

const FormInputNewPassword: React.FC<CustomFormInputProps> = (props) => {
  const [rulesVisible, setRulesVisible] = useState(false);
  const { name } = props;
  const [{}, { error, touched }] = useField(name);

  useEffect(() => {
    if (touched && !error) {
      setRulesVisible(false);
    }
  }, [error, touched]);

  return (
    <>
      <FormInputPassword
        {...props}
        onFocus={(): void => setRulesVisible(true)}
      />
      <Collapse in={rulesVisible}>
        <List data-testid="passwordRulesList">
          <RuleCheck rule={minLength} testid="passwordRuleMinLength" />
          <RuleCheck rule={hasLowerCase} testid="passwordRuleHasLowerCase" />
          <RuleCheck rule={hasUpperCase} testid="passwordRuleHasUpperCase" />
          <RuleCheck rule={hasNumbers} testid="passwordRuleHasNumbers" />
          <RuleCheck
            rule={hasSpecialCharacters}
            testid="passwordRuleHasSpecialCharacters"
          />
        </List>
      </Collapse>
    </>
  );
};

export default FormInputNewPassword;
